import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, Search, RefreshCw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function TutorParent() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [children, setChildren] = useState([]);
  const [busy, setBusy] = useState(false);
  const [autoLoaded, setAutoLoaded] = useState(false);

  // Auto-load: try /my-children first using the logged-in user's email
  useEffect(() => {
    if (!user) return;
    setBusy(true);
    api.get("/api/tutor/my-children").then(({ data }) => {
      setChildren(data.children);
      setEmail(data.parent_email);
      setAutoLoaded(true);
    }).catch(() => {}).finally(() => setBusy(false));
    // load-on-mount when user becomes available; setters are stable
  }, [user]);

  const load = async (e) => {
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/api/tutor/parent-view`);
      setChildren(data.children);
      setAutoLoaded(false);
      if (data.children.length === 0) toast.info("No students found for that parent email.");
    } catch {
      toast.error("Could not fetch.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="tutor-parent">
      <div className="label-eyebrow flex items-center gap-2"><Users className="w-3 h-3 text-[#D92D20]" /> Teacher4Me</div>
      <h1 className="font-display font-black text-3xl md:text-4xl mt-1">Parent dashboard</h1>

      {autoLoaded && children.length > 0 ? (
        <div className="mt-2 text-sm text-[#064E3B] inline-flex items-center gap-1.5" data-testid="parent-auto-banner">
          <CheckCircle2 className="w-4 h-4" />
          Auto-loaded {children.length} {children.length === 1 ? "child" : "children"} linked to <b>{user.email}</b>.
        </div>
      ) : (
        <p className="text-sm text-[#4B5563] mt-1">
          Look up children by the parent email they used at onboarding.
        </p>
      )}

      <div className="mt-4 flex gap-2 max-w-xl">
        <input data-testid="parent-email" type="email" value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="parent-email@example.com"
          className="flex-1 border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" />
        <button data-testid="parent-load" onClick={load} disabled={busy}
          className="btn-primary disabled:opacity-50 inline-flex items-center gap-1">
          <Search className="w-4 h-4" /> Look up
        </button>
        {autoLoaded && (
          <button data-testid="parent-refresh" onClick={load} disabled={busy}
            className="btn-outline inline-flex items-center gap-1" title="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        )}
      </div>

      {children.length === 0 && !busy && (
        <div className="mt-8 p-6 bg-white border border-dashed border-[#E5E7EB] rounded-2xl text-center text-sm text-[#4B5563]" data-testid="parent-empty">
          No children linked yet. Make sure your child entered <b>{email || "your email"}</b> as the parent email during onboarding.
        </div>
      )}

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {children.map((c, i) => (
          <div key={c.user_id || c.student_email || i} data-testid={`child-${i}`} className="bg-white border border-[#E5E7EB] rounded-2xl p-5">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-display font-bold text-lg">{c.student_name}</div>
                <div className="text-xs text-[#4B5563]">{c.student_email}</div>
              </div>
              {c.subscription ? (
                <span className="text-[10px] font-bold uppercase tracking-widest bg-[#064E3B] text-white px-2 py-0.5 rounded-full" data-testid={`child-${i}-plan`}>
                  {c.subscription.plan}
                </span>
              ) : (
                <span className="text-[10px] font-bold uppercase tracking-widest bg-[#F3F4F6] text-[#4B5563] px-2 py-0.5 rounded-full">
                  Free
                </span>
              )}
            </div>
            <div className="text-sm mt-2">{c.grade} · {c.province}</div>
            <div className="text-xs text-[#4B5563]">{c.school_name}</div>
            <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
              <div><b>{c.generations_this_month}</b> generations this month</div>
              <div><b>{c.tutor_messages_this_month}</b> tutor messages</div>
            </div>
            <div className="mt-3">
              <div className="text-xs font-semibold text-[#4B5563]">Recent activity</div>
              <ul className="mt-1 text-sm">
                {c.recent.length === 0 && <li className="text-[#4B5563]">No activity yet.</li>}
                {c.recent.map((r) => (
                  <li key={r.id} className="capitalize">{r.kind} — <span className="text-[#4B5563] text-xs">{new Date(r.created_at).toLocaleDateString()}</span></li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
